import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => localStorage.getItem('token');

const expenseReportsApiClient = axios.create({
  baseURL: config.api.expenseReportsServiceUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

expenseReportsApiClient.interceptors.request.use(
  (cfg) => {
    const token = getToken();
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error),
);

applyAuthRetryInterceptor(expenseReportsApiClient);
applyTokenRefreshInterceptor(expenseReportsApiClient);

const BASE = '/expense-reports';

/** The most expenses one review request may name (the service's MAX_REVIEW_ITEMS). */
export const MAX_REVIEW_ITEMS = 200;

// A failed request's message, for the per-item results of a batch that failed.
const requestErrorText = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return err?.message || 'The request failed.';
};

const expenseReportsService = {
  /** The signed-in user's own expenses, newest first. */
  getMine: async () => {
    const response = await expenseReportsApiClient.get(`${BASE}/mine`);
    return response.data.expenses;
  },

  /** values: { expense_date, amount, category, merchant, description } */
  submit: async (values) => {
    const response = await expenseReportsApiClient.post(`${BASE}/mine`, values);
    return response.data;
  },

  /** Only while the expense is pending. */
  update: async (expenseId, values) => {
    const response = await expenseReportsApiClient.put(`${BASE}/mine/${encodeURIComponent(expenseId)}`, values);
    return response.data;
  },

  /** Only while the expense is pending. */
  withdraw: async (expenseId) => {
    await expenseReportsApiClient.delete(`${BASE}/mine/${encodeURIComponent(expenseId)}`);
  },

  /**
   * Approver view: { expenses, totals, outside_range_count }. `status` is
   * pending | approved | rejected | paid, or 'all'; `range` ({ from, to }, ISO
   * dates, either may be null) limits by expense date. Totals always cover every
   * open expense.
   */
  getAll: async (status = 'pending', range = null) => {
    const params = status && status !== 'all' ? { status } : {};
    if (range?.from) params.date_from = range.from;
    if (range?.to) params.date_to = range.to;
    const response = await expenseReportsApiClient.get(`${BASE}/all`, { params });
    return response.data;
  },

  /**
   * An upload slot for one receipt file: { upload_id, url, fields, max_bytes }.
   * The file then goes straight to storage with postReceiptFile.
   */
  requestReceiptUpload: async ({ filename, contentType, size }) => {
    const response = await expenseReportsApiClient.post(`${BASE}/receipts/uploads`, {
      filename,
      content_type: contentType,
      size,
    });
    return response.data;
  },

  /**
   * POST a file to its upload slot: the slot's fields first, then the file. Uses
   * plain axios on purpose: the storage URL is presigned, and the app's auth
   * header would make S3 refuse it.
   */
  postReceiptFile: async (slot, file, onProgress) => {
    const form = new FormData();
    Object.entries(slot.fields || {}).forEach(([name, value]) => form.append(name, value));
    form.append('file', file);
    await axios.post(slot.url, form, {
      timeout: 120000,
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(event.loaded / event.total);
      },
    });
  },

  /** Owner: change receipts on your own expense. change: { add, remove, waiver }. */
  changeMyReceipts: async (expenseId, change) => {
    const response = await expenseReportsApiClient.post(`${BASE}/mine/${encodeURIComponent(expenseId)}/receipts`, change);
    return response.data;
  },

  /** Approver: change receipts on anyone's expense. */
  changeReceiptsAsApprover: async (expense, change) => {
    const response = await expenseReportsApiClient.post(`${BASE}/review/receipts`, {
      user_id: expense.user_id,
      expense_id: expense.expense_id,
      ...change,
    });
    return response.data;
  },

  /** A short-lived link to one of your own current receipts. disposition: inline | attachment. */
  myReceiptLink: async (expense, receiptId, disposition = 'inline') => {
    const path = `${BASE}/mine/${encodeURIComponent(expense.expense_id)}/receipts/${encodeURIComponent(receiptId)}`;
    const response = await expenseReportsApiClient.get(path, { params: { disposition } });
    return response.data;
  },

  /** Approver: a short-lived link to any receipt on an expense, removed ones included. */
  reviewReceiptLink: async (expense, receiptId, disposition = 'inline') => {
    const path = [BASE, 'review', 'receipts', expense.user_id, expense.expense_id, receiptId]
      .map((part, i) => (i < 3 ? part : encodeURIComponent(part)))
      .join('/');
    const response = await expenseReportsApiClient.get(path, { params: { disposition } });
    return response.data;
  },

  /** The IRS mileage rates, oldest first: [{ effective_from, rate_millis }]. */
  getMileageRates: async () => {
    const response = await expenseReportsApiClient.get(`${BASE}/mileage-rates`);
    return response.data.rates;
  },

  /**
   * Approver action on one or more expenses, as they were loaded: each item's
   * updated_at is sent, so one that changed since is refused rather than acted on.
   * items: [{ user_id, expense_id, updated_at }]; action: approve | reject | mark_paid | note.
   *
   * The service takes at most MAX_REVIEW_ITEMS per request, so bigger selections go
   * in batches. A batch that fails outright counts each of its items as failed and
   * the rest carry on; only if every batch fails is the error thrown.
   * Returns { updated, failed, results }.
   */
  review: async (items, action, note) => {
    const refs = items.map((e) => ({ user_id: e.user_id, expense_id: e.expense_id, updated_at: e.updated_at }));
    const merged = { updated: 0, failed: 0, results: [] };
    let firstError = null;
    let batchesOk = 0;
    for (let start = 0; start < refs.length; start += MAX_REVIEW_ITEMS) {
      const batch = refs.slice(start, start + MAX_REVIEW_ITEMS);
      const body = { items: batch, action };
      if (note && note.trim()) {
        body.note = note.trim();
      }
      try {
        // One batch at a time, so the results come back in the order sent.
        const { data } = await expenseReportsApiClient.post(`${BASE}/review`, body);
        merged.updated += data.updated;
        merged.failed += data.failed;
        merged.results.push(...data.results);
        batchesOk += 1;
      } catch (err) {
        firstError = firstError || err;
        const error = requestErrorText(err);
        merged.failed += batch.length;
        merged.results.push(...batch.map((ref) => ({ user_id: ref.user_id, expense_id: ref.expense_id, ok: false, error })));
      }
    }
    if (firstError && !batchesOk) throw firstError;
    return merged;
  },

  /**
   * Approver: edit or delete the note on history entry `entryIndex` of `expense`.
   * `entry` is that history entry as last loaded; its `at` and wording guard
   * against acting on a note someone else changed since (a 409).
   * action: 'edit' | 'delete'. Returns the updated expense.
   */
  changeNote: async (expense, entryIndex, entry, action, note) => {
    const body = {
      user_id: expense.user_id,
      expense_id: expense.expense_id,
      entry_index: entryIndex,
      entry_at: entry.at,
      entry_note: entry.note,
      action,
    };
    if (action === 'edit') {
      body.note = note;
    }
    const response = await expenseReportsApiClient.post(`${BASE}/review/note`, body);
    return response.data;
  },
};

export default expenseReportsService;
